import { Box, Text, Icon, VStack, Flex, useBreakpointValue } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useNotifications,
  useNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsReadFloat,
} from '../../hooks/useNotifications'
import { ArrowUpRight, EyeOff } from '../../utils/constants/icons'

const MotionBox = motion(Box)

const timeAgo = (inputDate: Date): string => {
  const now = new Date()
  const secondsAgo = Math.floor((now.getTime() - inputDate.getTime()) / 1000)

  const units = [
    { name: 'yr', seconds: 31536000 },
    { name: 'mo', seconds: 2592000 },
    { name: 'w', seconds: 604800 },
    { name: 'd', seconds: 86400 },
    { name: 'h', seconds: 3600 },
    { name: 'm', seconds: 60 },
    { name: 's', seconds: 1 },
  ]

  for (const unit of units) {
    const interval = Math.floor(secondsAgo / unit.seconds)
    if (interval >= 1) {
      return `${interval}${unit.name} ago`
    }
  }

  return 'just now'
}

const FloatingNotificationCard = ({
  notification,
  onDismiss,
  stackIndex,
  onClick,
  isStackHovered,
  isMobile,
}: {
  notification: any
  onDismiss: (id: string) => void
  stackIndex: number
  onClick: (notification: any) => void
  isStackHovered: boolean
  isMobile: boolean
}) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <MotionBox
      position="absolute"
      right={isMobile ? undefined : '0'}
      left={isMobile ? '0' : undefined}
      top={isMobile ? '0' : undefined}
      bottom={isMobile ? undefined : '0'}
      w={isMobile ? '100%' : '380px'}
      initial={isMobile ? { y: -80, opacity: 0, scale: 0.95 } : { x: 400, opacity: 0, scale: 0.8 }}
      animate={{
        x: isMobile ? 0 : isHovered ? -10 : 0,
        y: isMobile
          ? isStackHovered
            ? stackIndex * 90
            : stackIndex * 10
          : isStackHovered
          ? stackIndex * -90
          : stackIndex * -10,
        opacity: 1,
        scale: isStackHovered ? 1 : 1 - stackIndex * 0.05,
        rotate: isStackHovered ? 0 : stackIndex * 1,
        zIndex: 50 - stackIndex,
      }}
      exit={isMobile ? { y: -80, opacity: 0, scale: 0.95 } : { x: 400, opacity: 0, scale: 0.8 }}
      transition={{
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: stackIndex * 0.1,
      }}
      style={{
        originX: isMobile ? 0.5 : 1,
        originY: isMobile ? 0 : 0.5,
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <MotionBox
        bg="white"
        borderRadius="12px"
        border="2px solid"
        borderColor="neutral.50"
        p="12px 16px"
        cursor="pointer"
        onClick={() => onClick(notification)}
        position="relative"
        overflow="hidden"
        boxShadow={
          stackIndex === 0
            ? '0 8px 25px -5px rgba(14, 4, 157, 0.15), 0 4px 10px -3px rgba(0, 0, 0, 0.1)'
            : stackIndex === 1
            ? '0 6px 20px -5px rgba(0, 0, 0, 0.08), 0 3px 8px -3px rgba(0, 0, 0, 0.06)'
            : '0 4px 15px -5px rgba(0, 0, 0, 0.05), 0 2px 6px -3px rgba(0, 0, 0, 0.04)'
        }
        animate={{
          scale: isHovered ? 1.02 : 1,
          y: isHovered ? -4 : 0,
          boxShadow: isHovered
            ? '0 20px 40px -10px rgba(14, 4, 157, 0.25), 0 8px 20px -5px rgba(0, 0, 0, 0.15)'
            : '0 8px 25px -5px rgba(14, 4, 157, 0.15), 0 4px 10px -3px rgba(0, 0, 0, 0.1)',
          borderColor: 'neutral.50',
        }}
        transition={{
          duration: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
      >
        <MotionBox
          position="absolute"
          left="0"
          top="0"
          bottom="0"
          bg="primary.400"
          borderRadius="0 6px 6px 0"
          w="4px"
          initial={{ scaleY: 0 }}
          animate={{
            scaleY: 1,
            boxShadow:
              (isHovered && stackIndex === 0) || isStackHovered
                ? '0 0 12px rgba(14, 4, 157, 0.4)'
                : '0 0 0px rgba(14, 4, 157, 0)',
          }}
          transition={{ duration: 0.4, delay: 0.2 }}
        />

        <MotionBox
          position="absolute"
          right="12px"
          top="12px"
          w="6px"
          h="6px"
          bg="white"
          borderRadius="full"
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss(notification.id)
          }}
          _hover={{ bg: 'neutral.400' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0.6 }}
          transition={{ duration: 0.2 }}
        />

        <VStack align="start" spacing="8px" position="relative" zIndex={1}>
          <MotionBox
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <Text
              textStyle="b-xs"
              fontWeight="500"
              color={
                isStackHovered || stackIndex === 0 ? 'primary.500' : stackIndex === 1 ? 'neutral.500' : 'neutral.400'
              }
              letterSpacing="1"
            >
              {timeAgo(new Date(notification.created_at))}
            </Text>
          </MotionBox>

          <Text
            textStyle="sm"
            fontSize="13px"
            fontWeight="600"
            color={
              isStackHovered || stackIndex === 0 ? 'neutral.900' : stackIndex === 1 ? 'neutral.700' : 'neutral.600'
            }
            lineHeight="1.3"
            letterSpacing="0.1px"
            noOfLines={2}
          >
            {notification.body}
          </Text>
        </VStack>
      </MotionBox>
    </MotionBox>
  )
}

const FloatingNotifications = ({ isDrawerOpen, onOpen }: { isDrawerOpen: boolean; onOpen: () => void }) => {
  const [dismissedIds, setDismissedIds] = useState<string[]>([])
  const [visibleNotifications, setVisibleNotifications] = useState<any[]>([])
  const [isStackHovered, setIsStackHovered] = useState(false)
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false

  const notificationFilters = { page: 1, page_size: 10, search: '', archived: false, read: false }
  const countFilters = { archived: false, favorite: false, read: false }

  const notifications = useNotifications(notificationFilters, 30000)
  const notificationCount = useNotificationCount(countFilters, 30000)

  const markAsReadMutation = useMarkNotificationRead()
  const markAllAsReadMutation = useMarkAllNotificationsReadFloat(countFilters)

  useEffect(() => {
    if (notifications.data?.data) {
      const newNotifications = notifications.data.data
        .filter((notif: any) => !notif.read && !dismissedIds.includes(notif.id))
        .slice(0, 5)

      setVisibleNotifications(newNotifications)
    }
  }, [notifications.data, dismissedIds])

  const handleDismiss = (id: string) => {
    setDismissedIds((prev) => [...prev, id])
  }

  const handleClick = (notification: any) => {
    if (!notification.read) {
      handleDismiss(notification.id)
      markAsReadMutation.mutate({ id: notification.id, read: true })
    } else {
      handleDismiss(notification.id)
    }

    if (notification.navigate) {
      window.open(`/#/${notification.navigate}`, '_blank')
    }
  }

  const markAllAsRead = () => {
    setVisibleNotifications([])
    markAllAsReadMutation.mutate(undefined, {
      onSuccess: () => setDismissedIds([]),
      onError: () => {
        if (notifications.data?.data) {
          const unreadNotifications = notifications.data.data
            .filter((notif: any) => !notif.read && !dismissedIds.includes(notif.id))
            .slice(0, 5)
          setVisibleNotifications(unreadNotifications)
        }
      },
    })
  }

  const displayedNotifications = visibleNotifications.slice(0, 3)
  const totalUnreadCount = notificationCount.data?.count ?? 0
  const hiddenCount = Math.max(0, totalUnreadCount - 3)

  const shouldShowNotifications = !isDrawerOpen && visibleNotifications.length > 0

  return (
    <Box
      position="fixed"
      top={isMobile ? 'calc(env(safe-area-inset-top, 0px) + 16px)' : undefined}
      bottom={isMobile ? undefined : '24px'}
      left={isMobile ? '0' : undefined}
      right={isMobile ? '0' : '24px'}
      px={isMobile ? '16px' : undefined}
      zIndex={9999}
      pointerEvents="none"
    >
      <AnimatePresence mode="wait">
        {shouldShowNotifications && (
          <MotionBox
            mb={isMobile ? undefined : '45px'}
            mt={isMobile ? '0px' : undefined}
            mr="0px"
            position="relative"
            w={isMobile ? '100%' : '380px'}
            initial={isMobile ? { opacity: 0, scale: 0.95, y: -80 } : { opacity: 0, scale: 0.8, x: 400 }}
            animate={{
              h: isStackHovered ? '300px' : '200px',
              opacity: 1,
              scale: 1,
              x: 0,
              y: 0,
            }}
            exit={isMobile ? { opacity: 0, scale: 0.9, y: -80 } : { opacity: 0, scale: 0.7, x: 400 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onHoverStart={() => setIsStackHovered(true)}
            onHoverEnd={() => setIsStackHovered(false)}
            pointerEvents="auto"
            drag={isMobile ? 'y' : false}
            dragConstraints={isMobile ? { top: 0, bottom: 0 } : undefined}
            dragElastic={isMobile ? { top: 0.3, bottom: 0 } : undefined}
            onDragEnd={
              isMobile
                ? (_e, info) => {
                    if (info.offset.y < -60 || info.velocity.y < -300) {
                      markAllAsRead()
                    }
                  }
                : undefined
            }
          >
            <AnimatePresence mode="popLayout">
              {displayedNotifications.map((notification, index) => (
                <Box key={notification.id} pointerEvents="auto">
                  <FloatingNotificationCard
                    notification={notification}
                    onDismiss={handleDismiss}
                    onClick={handleClick}
                    stackIndex={index}
                    isStackHovered={isStackHovered}
                    isMobile={isMobile}
                  />
                </Box>
              ))}
            </AnimatePresence>
          </MotionBox>
        )}
      </AnimatePresence>

      {shouldShowNotifications && (
        <MotionBox
          position="absolute"
          right={isMobile ? '16px' : '0'}
          top={isMobile ? '210px' : undefined}
          bottom={isMobile ? undefined : '10px'}
          pointerEvents="auto"
          initial={{ opacity: 1, y: 10 }}
          transition={{ duration: 0.2 }}
        >
          <Flex gap="8px" align="center" justify="flex-end">
            {!isMobile && (
              <Flex
                align="center"
                gap="6px"
                bg="white"
                color="neutral.700"
                px="16px"
                py="6px"
                borderRadius="20px"
                cursor="pointer"
                fontSize="12px"
                fontWeight="600"
                boxShadow="0 4px 15px -3px rgba(0, 0, 0, 0.1)"
                border="1px solid"
                borderColor="neutral.100"
                onClick={markAllAsRead}
              >
                <Icon boxSize="18px" fill="transparent" color="primary.500">
                  <EyeOff />
                </Icon>
                <Text>Hide</Text>
              </Flex>
            )}

            {hiddenCount > 0 && (
              <Flex
                align="center"
                gap="6px"
                bg="primary.400"
                color="white"
                px="12px"
                py="6px"
                borderRadius="20px"
                cursor="pointer"
                fontSize="12px"
                fontWeight="600"
                boxShadow="0 4px 15px -3px rgba(14, 4, 157, 0.3)"
                onClick={onOpen}
              >
                <Text>and {hiddenCount} more</Text>
                <Icon boxSize="12px">
                  <ArrowUpRight />
                </Icon>
              </Flex>
            )}
          </Flex>
        </MotionBox>
      )}
    </Box>
  )
}

export default FloatingNotifications
